const JOURNEY_KEY = "tap2cash_marketing_journey";
const SIGNALS_KEY = "tap2cash_pending_marketing_signals";
const JOURNEY_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const readSession = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSession = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {}
};

const deleteSession = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {}
};

const validJourney = (raw) => {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    if (!value || typeof value !== "object" || value.expires_at <= Date.now()) {
      return null;
    }
    if (!Number.isFinite(value.expires_at)) return null;
    const journey = {};
    for (const field of ["tracking", "landing", "click", "completedClick"]) {
      if (typeof value[field] === "string") journey[field] = value[field];
    }
    return journey;
  } catch {
    return null;
  }
};

const saveJourney = (journey) => {
  const data = JSON.stringify({ ...journey, expires_at: Date.now() + JOURNEY_TTL_MS });
  try {
    localStorage.setItem(JOURNEY_KEY, data);
    deleteSession(JOURNEY_KEY);
    return true;
  } catch {
    try {
      sessionStorage.setItem(JOURNEY_KEY, data);
      return true;
    } catch {
      return false;
    }
  }
};

let legacyChecked = false;
const migrateLegacyJourney = () => {
  if (legacyChecked) return {};
  legacyChecked = true;
  const fields = {
    tracking: "tap2cash_active_tracking",
    landing: "tap2cash_pending_landing",
    click: "tap2cash_active_click",
    completedClick: "tap2cash_completed_click",
  };
  const journey = {};
  for (const [field, key] of Object.entries(fields)) {
    let value = null;
    try {
      const old = localStorage.getItem(`tap2cash_attribution_v2_${key}`);
      if (old) {
        const item = JSON.parse(old);
        if (typeof item.value === "string" && item.expires_at > Date.now()) {
          value = item.value;
        }
      }
    } catch {}
    if (value === null) value = readSession(key);
    if (value !== null) journey[field] = value;
  }
  if (!Object.keys(journey).length || !saveJourney(journey)) return journey;
  for (const key of Object.values(fields)) {
    try {
      localStorage.removeItem(`tap2cash_attribution_v2_${key}`);
    } catch {}
    deleteSession(key);
  }
  try {
    localStorage.removeItem("tap2cash_attribution_v2_initialized");
  } catch {}
  return journey;
};

const readJourney = () => {
  let stored = null;
  try {
    stored = localStorage.getItem(JOURNEY_KEY);
    const journey = validJourney(stored);
    if (journey) return journey;
    if (stored !== null) localStorage.removeItem(JOURNEY_KEY);
  } catch {}
  stored = readSession(JOURNEY_KEY);
  const journey = validJourney(stored);
  if (journey) return journey;
  if (stored !== null) deleteSession(JOURNEY_KEY);
  return migrateLegacyJourney();
};


let currentLocation = null;
let currentRouterKey = null;
let currentNavigationId = null;
const makeNavigationId = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (part) => {
    const value = Math.floor(Math.random() * 16);
    return (part === "x" ? value : (value & 3) | 8).toString(16);
  });
};

export const getNavigationId = () => {
  if (typeof window === "undefined") return null;
  const url = window.location.pathname + window.location.search;
  if (!currentNavigationId || currentLocation !== url) {
    currentLocation = url;
    currentNavigationId = makeNavigationId();
    currentRouterKey = null;
  }
  return currentNavigationId;
};

export const beginNavigation = (routerKey) => {
  getNavigationId();
  if (currentRouterKey !== null && currentRouterKey !== routerKey) {
    currentNavigationId = makeNavigationId();
  }
  currentRouterKey = routerKey;
  return currentNavigationId;
};

export const hasTrackingContext = () => {
  if (typeof window === "undefined") return false;
  capturePageAttribution();
  const journey = readJourney();
  return Boolean(journey.tracking || journey.landing);
};

const TRACKING_PARAMS = [
  "gclid",
  "gbraid",
  "wbraid",
  "gad_campaignid",
  "gad_source",
  "fbclid",
  "fbc",
  "fbp",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "campaign",
  "content",
  "keyword",
  "meta_campaign_id",
  "meta_campaign_name",
  "meta_adset_id",
  "meta_adset_name",
  "meta_ad_id",
  "meta_ad_name",
  "utm_ad_set",
  "utm_ad",
  "utm_placement",
  "utm_site_source",
  "placement",
  "site_source_name",
];

const paidTouchKey = (url) => {
  const query = new URL(url).searchParams;
  const source = (query.get("utm_source") || "").toLowerCase();
  const medium = (query.get("utm_medium") || "").toLowerCase();
  const organic = [
    "organic",
    "referral",
    "direct",
    "social",
    "email",
    "none",
  ].includes(medium);
  const paid = [
    "cpc",
    "ppc",
    "paid",
    "paidsearch",
    "paid_search",
    "paid-social",
    "paid_social",
    "paidsocial",
    "social_paid",
    "advertising",
    "ads",
  ].includes(medium);
  const campaign = Boolean(query.get("campaign") || query.get("utm_campaign"));
  const google =
    Boolean(
      query.get("gclid") ||
      query.get("gbraid") ||
      query.get("wbraid") ||
      query.get("gad_campaignid"),
    ) ||
    (["google", "googleads", "adwords"].includes(source) &&
      !organic &&
      (paid || campaign));
  const meta =
    Boolean(
      query.get("fbclid") ||
      query.get("meta_campaign_id") ||
      query.get("meta_campaign_name"),
    ) ||
    (["meta", "facebook", "instagram", "fb", "ig"].includes(source) &&
      !organic &&
      (paid || campaign));
  if (google === meta) return null;
  return JSON.stringify(
    TRACKING_PARAMS.filter((key) => query.get(key)).map((key) => [
      key,
      query.get(key),
    ]),
  );
};

export const safePageUrl = (url) => {
  const safe = new URL(url);
  [...safe.searchParams.keys()].forEach((key) => {
    if (!TRACKING_PARAMS.includes(key)) safe.searchParams.delete(key);
  });
  safe.hash = "";
  return safe.toString();
};

export const capturePageAttribution = () => {
  if (typeof window === "undefined") return;
  const click = paidTouchKey(window.location.href);
  const journey = readJourney();
  if (!click || journey.completedClick === click || journey.click === click) return;

  saveJourney({ ...journey, tracking: null, landing: safePageUrl(window.location.href), click });
  deleteSession(SIGNALS_KEY);
};

export const metaMatchingHeaders = () => {
  if (typeof document === "undefined") return {};
  const cookies = document.cookie.split(";").map((part) => part.trim());
  const headers = {};
  for (const [name, header] of [
    ["_fbp", "X-Meta-Fbp"],
    ["_fbc", "X-Meta-Fbc"],
  ]) {
    const pair = cookies.find((cookie) => cookie.startsWith(`${name}=`));
    if (!pair) continue;
    const value = pair.slice(name.length + 1);
    if (/^fb\.[0-9]+\.[0-9]{10,16}\.[a-zA-Z0-9_-]{1,200}$/.test(value)) {
      headers[header] = value;
    }
  }
  return headers;
};

// An ad click may contain fbclid but omit Meta's numeric {{campaign.id}}.
// Do not retry that unresolvable landing on every subsequent API request.
// Keep the click marker so actual Meta lead submission remains protected.
const invalidMetaLanding = (landing) => {
  try {
    const url = new URL(landing);
    const params = url.searchParams;
    const source = (params.get("utm_source") || "").toLowerCase();
    const looksMeta = Boolean(
      params.get("fbclid") || params.get("meta_campaign_id") ||
      params.get("meta_campaign_name") ||
      ["meta", "facebook", "instagram", "fb", "ig"].includes(source)
    );
    if (!looksMeta) return false;
    return ![params.get("meta_campaign_id"), params.get("utm_campaign")]
      .some((id) => typeof id === "string" && /^[1-9]\d*$/.test(id.trim()));
  } catch {
    return false;
  }
};

export const trackingRequestHeaders = (ignorePending = false) => {
  if (typeof window === "undefined") return {};
  capturePageAttribution();
  const journey = readJourney();
  const invalidPending = journey.landing && invalidMetaLanding(journey.landing);
  if (invalidPending) saveJourney({ ...journey, landing: null, tracking: null });
  const pending = ignorePending || invalidPending ? null : journey.landing;
  const tracking = invalidPending ? null : journey.tracking;
  const url = new URL(window.location.href);
  TRACKING_PARAMS.forEach((key) => url.searchParams.delete(key));
  const activeClick = journey.click;
  let metaJourney = false;
  if (activeClick) {
    try {
      const entries = JSON.parse(activeClick);
      metaJourney = entries.some(([key]) => ["fbclid", "meta_campaign_id", "meta_campaign_name"].includes(key)) ||
        entries.some(([key, value]) => key === "utm_source" && ["meta", "facebook", "instagram", "fb", "ig"].includes(value.toLowerCase()));
    } catch {}
  }
  const headers = {
    "X-Client-Platform": "web",
    ...(metaJourney ? { "X-Meta-Journey": "1" } : {}),
    "X-Page-URL": pending || url.toString(),
    "X-Navigation-ID": getNavigationId(),
    ...metaMatchingHeaders(),
  };
  if (tracking && !pending) headers["X-Tracking"] = tracking;
  return headers;
};

let firstRequest = null;

export const prepareAttributionRequest = async () => {
  capturePageAttribution();
  const click = attributionClickSnapshot();
  while (readJourney().landing && click === attributionClickSnapshot()) {
    if (!firstRequest || firstRequest.click !== click) {
      let release;
      const done = new Promise((resolve) => {
        release = resolve;
      });
      const owner = { click, done, release };
      firstRequest = owner;
      return { headers: trackingRequestHeaders(), click, owner };
    }
    await firstRequest.done;
  }
  if (click !== attributionClickSnapshot()) {
    return {
      headers: trackingRequestHeaders(true),
      click: attributionClickSnapshot(),
      owner: null,
    };
  }
  return { headers: trackingRequestHeaders(), click, owner: null };
};

export const finishAttributionRequest = (owner) => {
  if (!owner) return;
  owner.release();
  if (firstRequest === owner) firstRequest = null;
};

export const acceptTrackingResponse = (response) => {
  if (typeof window === "undefined") return;
  const reference = response.headers?.["x-tracking"];
  if (
    typeof reference !== "string" ||
    !/^(google|meta):[1-9]\d*:[0-9a-f]{32}$/.test(reference)
  )
    return;
  const journey = readJourney();
  if (response.config?._attributionClick !== (journey.click || null))
    return;
  saveJourney({ ...journey, tracking: reference, landing: null });
};

export const markLeadCompleted = () => {
  if (typeof window === "undefined") return;
  const journey = readJourney();
  saveJourney(journey.click ? { completedClick: journey.click } : {});
  deleteSession(SIGNALS_KEY);
};

export const attributionClickSnapshot = () =>
  typeof window === "undefined" ? null : readJourney().click || null;

const MAX_QUEUED_SIGNALS = 32;
const MAX_REQUEST_SIGNALS = 5;
const MAX_SIGNALS_HEADER_BYTES = 5500;

const readSignals = () => {
  try {
    const entries = JSON.parse(readSession(SIGNALS_KEY) || "[]");
    return Array.isArray(entries)
      ? entries
          .filter(
            (entry) =>
              entry &&
              ["page", "sell_form_open"].includes(entry.kind) &&
              typeof entry.navigation_id === "string" &&
              typeof entry.url === "string",
          )
          .slice(-MAX_QUEUED_SIGNALS)
      : [];
  } catch {
    return [];
  }
};

const putSignals = (signals) =>
  writeSession(SIGNALS_KEY, JSON.stringify(signals));

const queueMarketingSignal = (kind, url, navigationId) => {
  if (!navigationId || !hasTrackingContext()) return;
  let cleanUrl;
  try {
    cleanUrl = safePageUrl(url);
  } catch {
    return;
  }
  if (cleanUrl.length > 1800) return;
  const events = readSignals();
  const previous = events.find(
    (event) => event.kind === kind && event.navigation_id === navigationId,
  );
  if (previous) return previous;
  const signal = { kind, url: cleanUrl, navigation_id: navigationId };
  events.push(signal);
  putSignals(events.slice(-MAX_QUEUED_SIGNALS));
  return signal;
};

export const queuePageVisit = (url, navigationId) =>
  queueMarketingSignal("page", url, navigationId);

export const queueSellFormOpen = (url, navigationId) =>
  queueMarketingSignal("sell_form_open", url, navigationId);

export const queuedMarketingSignals = () => {
  const selected = [];
  for (const entry of readSignals()) {
    if (selected.length >= MAX_REQUEST_SIGNALS) break;
    if (JSON.stringify([...selected, entry]).length > MAX_SIGNALS_HEADER_BYTES)
      break;
    selected.push(entry);
  }
  return selected;
};

const reservedSignals = new Set();
const signalKey = (signal) => `${signal.kind}:${signal.navigation_id}`;

export const reserveMarketingSignals = () => {
  const signals = queuedMarketingSignals().filter(
    (signal) => !reservedSignals.has(signalKey(signal)),
  );
  if (!signals.length) return null;
  signals.forEach((signal) => reservedSignals.add(signalKey(signal)));
  return signals;
};

export const releaseMarketingSignals = (signals) => {
  if (!Array.isArray(signals)) return;
  signals.forEach((signal) => reservedSignals.delete(signalKey(signal)));
};

export const marketingSignalHeaders = (signals) =>
  signals?.length ? { "X-Marketing-Signals": JSON.stringify(signals) } : {};

export const acknowledgeMarketingSignals = (response) => {
  const header = response?.headers?.["x-marketing-ack"];
  if (typeof header !== "string" || !header) return;
  const accepted = new Set(
    header
      .split(",")
      .filter((id) =>
        /^(page|sell_form_open):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          id,
        ),
      ),
  );
  if (!accepted.size) return;
  putSignals(
    readSignals().filter(
      (event) => !accepted.has(`${event.kind}:${event.navigation_id}`),
    ),
  );
};
